import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, ErrorState, LoadingState, Notice, Spinner } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";

export const AC = "/school/academic";

export const useList = (path) => useQuery({ queryKey: ["ac", path], queryFn: () => fetcher(path) });

export const useMembersByRole = (roles) => {
  const q = useQuery({ queryKey: ["school-members"], queryFn: () => fetcher("/school/members") });
  return (q.data || []).filter((m) => roles.includes(m.role) && m.status === "active");
};

// Small form helper: posts, shows Indonesian errors, refreshes academic queries.
export const useSubmit = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async (fn, okMsg) => {
    setBusy(true);
    setErr("");
    try {
      await fn();
      toast.success(okMsg);
      qc.invalidateQueries({ queryKey: ["ac"] });
      return true;
    } catch (e) {
      setErr(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, run };
};

export const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} {...p} /></div>
);

export const Pick = ({ label, value, onChange, options, placeholder, testId }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} data-testid={testId}><SelectValue placeholder={placeholder || "Pilih"} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const FormCard = ({ title, onSubmit, busy, err, children, submitLabel, testId }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="rounded-2xl border bg-card p-5 space-y-4" noValidate data-testid={testId}>
    <h3 className="font-bold">{title}</h3>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">{children}</div>
    {err && <Notice tone="danger">{err}</Notice>}
    <Button type="submit" disabled={busy} data-testid={`${testId}-submit`}>{busy && <Spinner />} {submitLabel}</Button>
  </form>
);

export const ListState = ({ q, empty, children }) => {
  if (q.isLoading) return <LoadingState rows={2} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (!q.data.length) return <EmptyState title={empty.title} description={empty.description} />;
  return children(q.data);
};

export const post = (path, body) => api.post(`${AC}${path}`, body);
export const patch = (path, body) => api.patch(`${AC}${path}`, body);
